import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Scale, Save, User, Phone, Mail, MapPin,
  BookOpen, Globe, Clock, Star, Briefcase, BarChart2,
  FileText, MessageSquare, Gavel, Calendar, Award,
  PenLine, Eye, CheckCircle
} from 'lucide-react';

/* ── Shared styles ─────────────────────────────────────── */
const inputCls =
  'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white';
const textareaCls =
  'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none';

/* ── View-mode value display ───────────────────────────── */
function ViewValue({ value, fallback = '—' }) {
  return (
    <p className="text-sm text-gray-800 font-medium whitespace-pre-wrap">
      {value ? value : <span className="text-gray-400 font-normal">{fallback}</span>}
    </p>
  );
}

/* ── Section card ──────────────────────────────────────── */
function SectionCard({ icon: Icon, title, color = 'blue', children }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-100 text-blue-700',
    purple: 'bg-purple-50 border-purple-100 text-purple-700',
    green: 'bg-green-50 border-green-100 text-green-700',
    orange: 'bg-orange-50 border-orange-100 text-orange-700',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className={`flex items-center gap-2 px-5 py-3 border-b ${colors[color]}`}>
        <Icon size={16} />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

/* ── Progress bar (view mode) ──────────────────────────── */
function ProgressBar({ value, color = 'bg-blue-500', bg = 'bg-blue-100' }) {
  const pct = Math.min(parseFloat(value) || 0, 100);
  return (
    <div className={`mt-1.5 ${bg} rounded-full h-2 overflow-hidden`}>
      <div className={`${color} h-2 rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Main Page
══════════════════════════════════════════════════════════ */
export default function JudgeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /* mode: 'view' | 'edit' */
  const mode = searchParams.get('mode') === 'edit' ? 'edit' : 'view';
  const isEdit = mode === 'edit';

  const [judge, setJudge] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date_of_birth: '', phone: '', email: '', office_address: '',
    education: '', languages_spoken: '', years_of_service: '',
    specialization: '', notable_cases: '', case_load_per_year: '',
    case_types_handled: '', avg_resolution_days: '', judgment_rate: '',
    settlement_rate: '', ruling_patterns: '', communication_style: '',
    courtroom_behavior: '',
  });

  useEffect(() => {
    api.get(`/masters/judges/${id}/`).then(r => setJudge(r.data)).catch(() => {});
    api.get(`/masters/judges/${id}/profile/`).then(r => {
      const d = r.data;
      setForm({
        date_of_birth: d.date_of_birth || '',
        phone: d.phone || '',
        email: d.email || '',
        office_address: d.office_address || '',
        education: d.education || '',
        languages_spoken: d.languages_spoken || '',
        years_of_service: d.years_of_service ?? '',
        specialization: d.specialization || '',
        notable_cases: d.notable_cases || '',
        case_load_per_year: d.case_load_per_year ?? '',
        case_types_handled: d.case_types_handled || '',
        avg_resolution_days: d.avg_resolution_days ?? '',
        judgment_rate: d.judgment_rate ?? '',
        settlement_rate: d.settlement_rate ?? '',
        ruling_patterns: d.ruling_patterns || '',
        communication_style: d.communication_style || '',
        courtroom_behavior: d.courtroom_behavior || '',
      });
    }).catch(() => {});
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ['years_of_service', 'case_load_per_year', 'avg_resolution_days'].forEach(k => {
        payload[k] = payload[k] !== '' ? parseInt(payload[k]) : null;
      });
      ['judgment_rate', 'settlement_rate'].forEach(k => {
        payload[k] = payload[k] !== '' ? parseFloat(payload[k]) : null;
      });
      payload.date_of_birth = payload.date_of_birth || null;
      await api.patch(`/masters/judges/${id}/profile/`, payload);
      toast.success('Judge profile saved successfully!');
      setSearchParams({ mode: 'view' });       // switch to view after save
    } catch {
      toast.error('Failed to save profile');
    }
    setSaving(false);
  };

  const age = form.date_of_birth
    ? Math.floor((new Date() - new Date(form.date_of_birth)) / (365.25 * 24 * 3600 * 1000))
    : null;

  /* ── Helper: field renders differently by mode ───────── */
  const F = {
    text: (key, placeholder) => isEdit
      ? <input type="text" className={inputCls} placeholder={placeholder} value={form[key]} onChange={e => set(key, e.target.value)} />
      : <ViewValue value={form[key]} />,

    email: (key) => isEdit
      ? <input type="email" className={inputCls} placeholder="judge@court.gov.in" value={form[key]} onChange={e => set(key, e.target.value)} />
      : <ViewValue value={form[key]} />,

    date: (key) => isEdit
      ? <input type="date" className={inputCls} value={form[key]} onChange={e => set(key, e.target.value)} />
      : <ViewValue value={form[key]} />,

    number: (key, placeholder, min = '0', max = undefined) => isEdit
      ? <input type="number" min={min} max={max} step={max === '100' ? '0.01' : '1'} className={inputCls} placeholder={placeholder} value={form[key]} onChange={e => set(key, e.target.value)} />
      : <ViewValue value={form[key]} />,

    textarea: (key, placeholder, rows = 3) => isEdit
      ? <textarea className={textareaCls} rows={rows} placeholder={placeholder} value={form[key]} onChange={e => set(key, e.target.value)} />
      : <ViewValue value={form[key]} />,
  };

  /* ════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-5 max-w-5xl mx-auto">

      {/* ── Page Header ─────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/masters')}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Judge Profile</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {isEdit ? 'Edit judge profile details' : 'Viewing judge profile'}
            </p>
          </div>
        </div>

        {/* Toggle + Save buttons */}
        <div className="flex items-center gap-2">
          {isEdit ? (
            <>
              <button
                onClick={() => setSearchParams({ mode: 'view' })}
                className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors"
              >
                <Eye size={15} /> View Profile
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
              >
                <Save size={15} />
                {saving ? 'Saving…' : 'Save Profile'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setSearchParams({ mode: 'edit' })}
              className="flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
            >
              <PenLine size={15} /> Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* ── Mode Badge ──────────────────────────────────── */}
      <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium
        ${isEdit ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
        {isEdit ? <PenLine size={11} /> : <CheckCircle size={11} />}
        {isEdit ? 'Edit Mode — changes not saved until you click Save Profile' : 'View Mode — read only'}
      </div>

      {/* ── Judge Identity Card ──────────────────────────── */}
      {judge && (
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-2xl p-5 text-white flex items-center gap-4">
          <div className="bg-white/20 rounded-full p-3">
            <Scale size={28} className="text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold">{judge.name}</h2>
            {judge.designation && <p className="text-blue-200 text-sm">{judge.designation}</p>}
            {judge.court_name && (
              <p className="text-blue-300 text-xs mt-0.5 flex items-center gap-1">
                <MapPin size={11} /> {judge.court_name}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {age !== null && (
              <div className="bg-white/15 rounded-xl px-3 py-1.5 text-center">
                <p className="text-2xl font-bold">{age}</p>
                <p className="text-blue-200 text-xs">Years Old</p>
              </div>
            )}
            {form.years_of_service && (
              <div className="bg-white/15 rounded-xl px-3 py-1.5 text-center">
                <p className="text-2xl font-bold">{form.years_of_service}</p>
                <p className="text-blue-200 text-xs">Yrs Service</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Sections Grid ───────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Personal Information */}
        <SectionCard icon={User} title="Personal Information" color="blue">
          <Field label="Date of Birth">
            <div className="relative">
              <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              {isEdit
                ? <input type="date" className={inputCls + ' pl-8'} value={form.date_of_birth} onChange={e => set('date_of_birth', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.date_of_birth} /></div>}
            </div>
            {age !== null && <p className="text-xs text-gray-400 mt-1">Age: {age} years</p>}
          </Field>
          <Field label="Phone Number">
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              {isEdit
                ? <input type="text" className={inputCls + ' pl-8'} placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={e => set('phone', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.phone} /></div>}
            </div>
          </Field>
          <Field label="Email">
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              {isEdit
                ? <input type="email" className={inputCls + ' pl-8'} placeholder="judge@court.gov.in" value={form.email} onChange={e => set('email', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.email} /></div>}
            </div>
          </Field>
          <Field label="Office Address">
            <div className="relative">
              <MapPin size={14} className="absolute left-3 top-3 text-gray-400" />
              {isEdit
                ? <textarea className={textareaCls + ' pl-8'} rows={3} placeholder="Chamber / Office address" value={form.office_address} onChange={e => set('office_address', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.office_address} /></div>}
            </div>
          </Field>
        </SectionCard>

        {/* Professional Background */}
        <SectionCard icon={Award} title="Professional Background" color="purple">
          <Field label="Education & Qualifications">
            <div className="relative">
              <BookOpen size={14} className="absolute left-3 top-3 text-gray-400" />
              {isEdit
                ? <textarea className={textareaCls + ' pl-8'} rows={3} placeholder="e.g. LLB – Pune University, LLM – Delhi University" value={form.education} onChange={e => set('education', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.education} /></div>}
            </div>
          </Field>
          <Field label="Languages Spoken">
            <div className="relative">
              <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              {isEdit
                ? <input type="text" className={inputCls + ' pl-8'} placeholder="e.g. Marathi, Hindi, English" value={form.languages_spoken} onChange={e => set('languages_spoken', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.languages_spoken} /></div>}
            </div>
          </Field>
          <Row>
            <Field label="Years of Service">
              <div className="relative">
                <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {isEdit
                  ? <input type="number" min="0" className={inputCls + ' pl-8'} placeholder="e.g. 15" value={form.years_of_service} onChange={e => set('years_of_service', e.target.value)} />
                  : <div className="pl-7"><ViewValue value={form.years_of_service} /></div>}
              </div>
            </Field>
            <Field label="Specialization / Expertise">
              <div className="relative">
                <Star size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {isEdit
                  ? <input type="text" className={inputCls + ' pl-8'} placeholder="e.g. Civil, Criminal, Family" value={form.specialization} onChange={e => set('specialization', e.target.value)} />
                  : <div className="pl-7"><ViewValue value={form.specialization} /></div>}
              </div>
            </Field>
          </Row>
          <Field label="Notable Cases">
            <div className="relative">
              <FileText size={14} className="absolute left-3 top-3 text-gray-400" />
              {isEdit
                ? <textarea className={textareaCls + ' pl-8'} rows={3} placeholder="List significant cases presided over…" value={form.notable_cases} onChange={e => set('notable_cases', e.target.value)} />
                : <div className="pl-7"><ViewValue value={form.notable_cases} /></div>}
            </div>
          </Field>
        </SectionCard>

        {/* Case Statistics */}
        <SectionCard icon={BarChart2} title="Case Statistics" color="green">
          <Row>
            <Field label="Case Load per Year">
              <div className="relative">
                <Briefcase size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {isEdit
                  ? <input type="number" min="0" className={inputCls + ' pl-8'} placeholder="e.g. 250" value={form.case_load_per_year} onChange={e => set('case_load_per_year', e.target.value)} />
                  : <div className="pl-7"><ViewValue value={form.case_load_per_year} /></div>}
              </div>
            </Field>
            <Field label="Avg. Resolution Time (Days)">
              <div className="relative">
                <Clock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {isEdit
                  ? <input type="number" min="0" className={inputCls + ' pl-8'} placeholder="e.g. 180" value={form.avg_resolution_days} onChange={e => set('avg_resolution_days', e.target.value)} />
                  : <div className="pl-7"><ViewValue value={form.avg_resolution_days} /></div>}
              </div>
            </Field>
          </Row>
          <Field label="Case Types Handled">
            {isEdit
              ? <input type="text" className={inputCls} placeholder="e.g. Civil, Criminal, Family, Revenue" value={form.case_types_handled} onChange={e => set('case_types_handled', e.target.value)} />
              : <ViewValue value={form.case_types_handled} />}
          </Field>
          <Row>
            <Field label="Judgment Rate (%)">
              <div className="relative">
                <Gavel size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {isEdit
                  ? <input type="number" min="0" max="100" step="0.01" className={inputCls + ' pl-8'} placeholder="e.g. 72.50" value={form.judgment_rate} onChange={e => set('judgment_rate', e.target.value)} />
                  : <div className="pl-7"><ViewValue value={form.judgment_rate ? `${form.judgment_rate}%` : ''} /></div>}
              </div>
              {form.judgment_rate !== '' && <ProgressBar value={form.judgment_rate} color="bg-green-500" bg="bg-green-100" />}
            </Field>
            <Field label="Settlement Rate (%)">
              <div className="relative">
                <Scale size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                {isEdit
                  ? <input type="number" min="0" max="100" step="0.01" className={inputCls + ' pl-8'} placeholder="e.g. 27.50" value={form.settlement_rate} onChange={e => set('settlement_rate', e.target.value)} />
                  : <div className="pl-7"><ViewValue value={form.settlement_rate ? `${form.settlement_rate}%` : ''} /></div>}
              </div>
              {form.settlement_rate !== '' && <ProgressBar value={form.settlement_rate} color="bg-blue-500" bg="bg-blue-100" />}
            </Field>
          </Row>
        </SectionCard>

        {/* Behavioral Analysis */}
        <SectionCard icon={MessageSquare} title="Behavioral Analysis" color="orange">
          <Field label="Patterns in Rulings">
            {isEdit
              ? <textarea className={textareaCls} rows={3} placeholder="e.g. Tends to favour plaintiffs in property disputes…" value={form.ruling_patterns} onChange={e => set('ruling_patterns', e.target.value)} />
              : <ViewValue value={form.ruling_patterns} />}
          </Field>
          <Field label="Communication Style">
            {isEdit
              ? <textarea className={textareaCls} rows={3} placeholder="e.g. Highly formal, concise written orders…" value={form.communication_style} onChange={e => set('communication_style', e.target.value)} />
              : <ViewValue value={form.communication_style} />}
          </Field>
          <Field label="Courtroom Behavior">
            {isEdit
              ? <textarea className={textareaCls} rows={3} placeholder="e.g. Strict on timekeeping; courteous but firm with attorneys…" value={form.courtroom_behavior} onChange={e => set('courtroom_behavior', e.target.value)} />
              : <ViewValue value={form.courtroom_behavior} />}
          </Field>
        </SectionCard>

      </div>

      {/* ── Bottom action bar (edit mode only) ──────────── */}
      {isEdit && (
        <div className="flex justify-end gap-3 pb-4">
          <button
            onClick={() => setSearchParams({ mode: 'view' })}
            className="flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
          >
            <Eye size={15} /> View Profile
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-60"
          >
            <Save size={15} />
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      )}
    </div>
  );
}
